import { useEffect, useMemo, useState } from "react";
import AdminTopbar from "../../components/topbars/AdminTopbar";
import { IconSearch } from "../../components/icons/IconsAdmin";
import { getPlanes, crearPlan, getServicios, agregarCobertura, modificarCobertura } from "../../api/apiResources";
import "./Planes.css";

const NIVEL_COBERTURA_LABELS = {
    TOTAL: "Total (100%)",
    PARCIAL: "Parcial",
    NO_CUBIERTA: "No cubierta",
};

const NIVEL_COBERTURA_OPTIONS = ["TOTAL", "PARCIAL", "NO_CUBIERTA"];

const normalizePlan = (plan) => ({
    id: plan._id || plan.id,
    nombre: plan.nombre || "Sin nombre",
    coberturas: Array.isArray(plan.coberturas) ? plan.coberturas : [],
    activo: plan.activo !== false,
});

const formatPorcentaje = (cob) => {
    if (cob.nivelCobertura === "TOTAL") return "100%";
    if (cob.nivelCobertura === "NO_CUBIERTA") return "–";
    if (cob.porcentaje != null) return `${Math.round(cob.porcentaje * 100)}%`;
    if (cob.tarifaFija != null) return `$${cob.tarifaFija}`;
    return "Parcial";
};

const EMPTY_COBERTURA = { servicioId: "", nivelCobertura: "PARCIAL", porcentaje: "80", tarifaFija: "" };

const CoberturaForm = ({ servicios, onGuardar, onCancelar, initial = null }) => {
    const [form, setForm] = useState(initial ?? EMPTY_COBERTURA);

    const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const handleSubmit = () => {
        if (!form.servicioId) return alert("Seleccioná un servicio.");
        const payload = {
            servicio: form.servicioId,
            nivelCobertura: form.nivelCobertura,
        };
        if (form.nivelCobertura === "PARCIAL") {
            if (form.porcentaje !== "") payload.porcentaje = Number(form.porcentaje) / 100;
            if (form.tarifaFija !== "") payload.tarifaFija = Number(form.tarifaFija);
        }
        onGuardar(payload, form.servicioId);
    };

    return (
        <div className="planes-cobertura-form">
            <div className="form-row">
                <label className="form-field">
                    <span>Servicio</span>
                    <select value={form.servicioId} onChange={(e) => set("servicioId", e.target.value)}>
                        <option value="">Seleccioná un servicio...</option>
                        {servicios.map((s) => (
                            <option key={s.id} value={s.id}>{s.nombre}</option>
                        ))}
                    </select>
                </label>
                <label className="form-field">
                    <span>Nivel de cobertura</span>
                    <select value={form.nivelCobertura} onChange={(e) => set("nivelCobertura", e.target.value)}>
                        {NIVEL_COBERTURA_OPTIONS.map((n) => (
                            <option key={n} value={n}>{NIVEL_COBERTURA_LABELS[n]}</option>
                        ))}
                    </select>
                </label>
            </div>
            {form.nivelCobertura === "PARCIAL" && (
                <div className="form-row">
                    <label className="form-field">
                        <span>Porcentaje que cubre (0–100)</span>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            value={form.porcentaje}
                            onChange={(e) => set("porcentaje", e.target.value)}
                            placeholder="Ej: 80"
                        />
                    </label>
                    <label className="form-field">
                        <span>O tarifa fija que cubre ($)</span>
                        <input
                            type="number"
                            min="0"
                            value={form.tarifaFija}
                            onChange={(e) => set("tarifaFija", e.target.value)}
                            placeholder="Ej: 5000"
                        />
                    </label>
                </div>
            )}
            <div className="planes-cobertura-form__actions">
                <button className="btn-ghost" onClick={onCancelar}>Cancelar</button>
                <button className="primary-button" onClick={handleSubmit}>Guardar cobertura</button>
            </div>
        </div>
    );
};

const PlanRow = ({ plan, servicios, onCoberturaAgregada }) => {
    const [expanded, setExpanded] = useState(false);
    const [agregando, setAgregando] = useState(false);
    const [editandoServicioId, setEditandoServicioId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleAgregarCobertura = async (payload) => {
        setSaving(true);
        setError("");
        try {
            await agregarCobertura(plan.id, payload);
            setAgregando(false);
            onCoberturaAgregada();
        } catch (e) {
            setError(e.message || "Error al agregar cobertura.");
        } finally {
            setSaving(false);
        }
    };

    const handleModificarCobertura = async (payload, servicioId) => {
        setSaving(true);
        setError("");
        try {
            await modificarCobertura(plan.id, servicioId, payload);
            setEditandoServicioId(null);
            onCoberturaAgregada();
        } catch (e) {
            setError(e.message || "Error al modificar cobertura.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`plan-row${expanded ? " plan-row--expanded" : ""}`}>
            <div className="plan-row__header" onClick={() => setExpanded((v) => !v)}>
                <div className="plan-row__info">
                    <span className="plan-row__nombre">{plan.nombre}</span>
                    <span className="plan-row__coberturas-count">
                        {plan.coberturas.length} cobertura{plan.coberturas.length !== 1 ? "s" : ""}
                    </span>
                </div>
                <div className="plan-row__chevron">{expanded ? "▲" : "▼"}</div>
            </div>

            {expanded && (
                <div className="plan-row__body">
                    {plan.coberturas.length === 0 && (
                        <p className="plan-row__empty">Sin coberturas configuradas.</p>
                    )}
                    {plan.coberturas.length > 0 && (
                        <table className="coberturas-table">
                            <thead>
                            <tr>
                                <th>Servicio</th>
                                <th>Nivel</th>
                                <th>Cobertura</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {plan.coberturas.map((cob, i) => {
                                const servicio = cob.servicio;
                                const servicioId = servicio?._id || servicio?.id || cob.servicioId;
                                const servicioNombre = servicio?.nombre || servicios.find((s) => s.id === servicioId)?.nombre || "–";
                                return (
                                    <tr key={i}>
                                        <td>{servicioNombre}</td>
                                        <td>
                                                <span className={`nivel-badge nivel-badge--${(cob.nivelCobertura || "").toLowerCase()}`}>
                                                    {NIVEL_COBERTURA_LABELS[cob.nivelCobertura] || cob.nivelCobertura}
                                                </span>
                                        </td>
                                        <td>{formatPorcentaje(cob)}</td>
                                        <td>
                                            <button
                                                className="btn-ghost"
                                                style={{ fontSize: 12, padding: "4px 10px" }}
                                                onClick={() => setEditandoServicioId(editandoServicioId === servicioId ? null : servicioId)}
                                            >
                                                Editar
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    )}

                    {plan.coberturas.map((cob, i) => {
                        const servicioId = cob.servicio?._id || cob.servicio?.id || cob.servicioId;
                        if (editandoServicioId !== servicioId) return null;
                        const initial = {
                            servicioId,
                            nivelCobertura: cob.nivelCobertura || "PARCIAL",
                            porcentaje: cob.porcentaje != null ? String(Math.round(cob.porcentaje * 100)) : "",
                            tarifaFija: cob.tarifaFija != null ? String(cob.tarifaFija) : "",
                        };
                        return (
                            <div key={`edit-${i}`} className="plan-row__edit-form">
                                <p className="plan-row__edit-title">Editar cobertura</p>
                                <CoberturaForm
                                    servicios={servicios}
                                    initial={initial}
                                    onGuardar={handleModificarCobertura}
                                    onCancelar={() => setEditandoServicioId(null)}
                                />
                            </div>
                        );
                    })}

                    {error && <p className="plan-row__error">{error}</p>}

                    {agregando ? (
                        <div className="plan-row__add-form">
                            <p className="plan-row__edit-title">Nueva cobertura</p>
                            <CoberturaForm
                                servicios={servicios}
                                onGuardar={handleAgregarCobertura}
                                onCancelar={() => { setAgregando(false); setError(""); }}
                            />
                        </div>
                    ) : (
                        <button
                            className="btn-ghost plan-row__add-btn"
                            onClick={() => { setAgregando(true); setError(""); }}
                            disabled={saving}
                        >
                            + Agregar cobertura
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const Planes = () => {
    const [planes, setPlanes] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [creando, setCreando] = useState(false);
    const [nuevoPlan, setNuevoPlan] = useState("");
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const loadAll = async () => {
        setLoading(true);
        setError("");
        try {
            const [planesData, serviciosData] = await Promise.all([getPlanes(), getServicios()]);
            setPlanes(planesData.map(normalizePlan));
            setServicios(serviciosData.map((s) => ({ id: s._id || s.id, nombre: s.nombre })));
        } catch (e) {
            setError(e.message || "Error al cargar datos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []);

    const filtrados = useMemo(() =>
            planes.filter((p) => p.nombre.toLowerCase().includes(query.toLowerCase())),
        [planes, query]
    );

    const handleCrearPlan = async () => {
        if (!nuevoPlan.trim()) return setSaveError("Ingresá un nombre para el plan.");
        setSaving(true);
        setSaveError("");
        try {
            await crearPlan({ nombre: nuevoPlan.trim() });
            setNuevoPlan("");
            setCreando(false);
            await loadAll();
        } catch (e) {
            setSaveError(e.message || "Error al crear el plan.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="planes-page">
            <AdminTopbar
                title="Planes"
                subtitle={`${planes.length} plan${planes.length !== 1 ? "es" : ""} configurado${planes.length !== 1 ? "s" : ""}`}
                actionLabel="+ Nuevo plan"
                onAction={() => { setCreando(true); setSaveError(""); }}
            />

            <div className="planes-page__body">
                <div className="planes-search">
                    <IconSearch />
                    <input
                        type="text"
                        placeholder="Buscar plan..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>

                {creando && (
                    <div className="planes-create-form">
                        <label className="form-field">
                            <span>Nombre del plan</span>
                            <input
                                type="text"
                                value={nuevoPlan}
                                onChange={(e) => setNuevoPlan(e.target.value)}
                                placeholder="Ej: Plan Básico"
                                autoFocus
                            />
                        </label>
                        {saveError && <p className="planes-create-form__error">{saveError}</p>}
                        <div className="planes-create-form__actions">
                            <button className="btn-ghost" onClick={() => { setCreando(false); setSaveError(""); }}>
                                Cancelar
                            </button>
                            <button className="primary-button" onClick={handleCrearPlan} disabled={saving}>
                                {saving ? "Guardando..." : "Crear plan"}
                            </button>
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="planes-loading">
                        <div className="patient-state__loader" />
                        <p>Cargando planes...</p>
                    </div>
                )}

                {!loading && error && <p className="planes-error">{error}</p>}

                {!loading && !error && filtrados.length === 0 && (
                    <p className="planes-empty">
                        {query ? "No hay planes que coincidan con la búsqueda." : "No hay planes creados todavía."}
                    </p>
                )}

                {!loading && !error && (
                    <div className="planes-lista">
                        {filtrados.map((plan) => (
                            <PlanRow
                                key={plan.id}
                                plan={plan}
                                servicios={servicios}
                                onCoberturaAgregada={loadAll}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Planes;